/* eslint-disable  no-param-reassign, sonarjs/cognitive-complexity */
import Link from 'next/link';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NAVIGATION_LAYOUT } from '../../../brands/default/constants';
import { HEADER_NAV_POPOVER } from '../../../brands/default/content/components/header';
import {
  ENABLE_GROUP_NAV,
  ENABLE_NATIONAL_NAV,
} from '../../../brands/default/features';
import { hashCode } from '../../../helpers/hash';
import ThemeContext from '../../../providers/ThemeProvider';
import { getDealPreviews } from '../../../redux/actions/navigation';
import BlackFridayHeaderNavigationPopover from './BlackFridayHeaderNavigationPopover';
import HeaderNavigationDealCard from './DealCard';
import GroupedHeaderNavigationPopover from './GroupedHeaderNavigationPopover';
import HeaderNavigationNationalPopover from './HeaderNavigationNationalPopover';

const HeaderNavigationPopover = ({
  category,
  setOpen,
  showCount = true,
  site,
}) => {
  const theme = useContext(ThemeContext);
  const isBlackFriday = category?.linkText === 'Black Friday Deals';
  const enableGroupedNavigation =
    ENABLE_GROUP_NAV &&
    NAVIGATION_LAYOUT.groupedCatgory.includes(category?.linkText);
  const enableNationalNavigation =
    ENABLE_NATIONAL_NAV &&
    NAVIGATION_LAYOUT.nationalCategory.includes(category?.shortName);
  const dispatch = useDispatch();
  const previews = useSelector((state) => state.navigation.previews);
  const deals = useMemo(() => previews[category.url], [previews, category.url]);
  const totalDealsCount = category.subNavigations.reduce((out, item) => {
    if (item.count > 0) {
      out += item.count;
    }

    return out;
  }, 0);

  useEffect(() => {
    // if we haven't got the deals yet -- try, if no deals returned it will be null so only retry on undefined
    if (deals === undefined) {
      dispatch(getDealPreviews(category.url, 4));
    }
  }, [dispatch, category.url, deals]);

  // we only show the popover if there is a sub navigation to show
  if (category.subNavigations.length === 0) {
    return null;
  }

  return (
    <>
      {enableGroupedNavigation ? (
        <GroupedHeaderNavigationPopover
          category={category}
          deals={deals}
          navCat={category.linkText}
          site={site}
        />
      ) : isBlackFriday ? (
        <BlackFridayHeaderNavigationPopover category={category} />
      ) : enableNationalNavigation ? (
        <HeaderNavigationNationalPopover
          category={category}
          setOpen={setOpen}
          site={site}
        />
      ) : (
        <div className="navigation-popover">
          <div className="navigation-popover__column">
            <Link href={category.url ?? ''}>
              <a className="navigation-popover__link">
                {HEADER_NAV_POPOVER.allCategoryDeals(category.linkText)}{' '}
                {totalDealsCount > 0 && showCount && `(${totalDealsCount})`}
              </a>
            </Link>
          </div>
          <div className="navigation-popover__column">
            <ul className="navigation-popover__list">
              <li>
                <Link href={category.url ?? ''}>
                  <a className="navigation-popover__link navigation-popover__link--small-only">
                    {HEADER_NAV_POPOVER.allCategoryDeals(category.linkText)}{' '}
                    {totalDealsCount > 0 && showCount && `(${totalDealsCount})`}
                  </a>
                </Link>
              </li>
              {category.subNavigations.map((item, index) => (
                <li key={`HNP_li_${index}_${hashCode()}`}>
                  <Link href={item.url ?? ''}>
                    <a
                      className="navigation-popover__link"
                      onClick={() => setOpen(false)}
                      onKeyDown={() => setOpen(false)}
                      role="link"
                      tabIndex="0"
                    >
                      {item.linkText}{' '}
                      {item.count && showCount && `(${item.count})`}
                    </a>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="navigation-popover__column navigation-popover__deals">
            {deals?.map((deal) => (
              <div
                className="navigation-popover__inner"
                key={`HNP_dealcard_${deal.id}_${hashCode()}`}
              >
                <HeaderNavigationDealCard deal={deal} site={site} />
              </div>
            ))}
            {!deals &&
              [null, null, null, null].map((_, index) => {
                return (
                  <div
                    className="deal-card--placeholder"
                    key={`HNP_deal_card_${index}_${hashCode()}`}
                  />
                );
              })}
          </div>
        </div>
      )}
      <style jsx>{`
        .navigation-popover__column {
          padding: 0 15px;
          border-right: 1px solid #cccccc;
          flex-grow: 1;
          flex-basis: 0;
        }
        .navigation-popover__list {
          list-style: none;
          padding: 0;
        }
        .navigation-popover__column:first-child {
          padding-left: 1%;
        }
        .navigation-popover__column:last-child {
          border-right: none;
          padding-right: 0;
        }
        .navigation-popover__link {
          color: #000000;
        }
        .navigation-popover__link--small-only {
          display: none;
        }
        .navigation-popover__link:hover {
          color: ${theme.colors.navlinkactive};
        }
        .navigation-popover__deals {
          align-item: top;
          display: flex;
          flex-wrap: wrap;
          min-width: 600px;
          padding: 0 8px;
          margin-top: -15px;
          width: 600px;
        }
        .navigation-popover__inner {
          display: flex;
          padding: 15px 7px 0;
          width: 50%;
        }
        .deal-card--placeholder {
          width: 100%;
          padding-bottom: 66.9%;
          background-color: #eeeeee;
        }

        @media (max-width: ${theme.breakpoints.xlDown}) {
          .navigation-popover__link--small-only {
            display: block;
          }
          .navigation-popover__column:first-child {
            display: none;
          }
          .navigation-popover__column:nth-child(2) {
            padding-left: 0;
          }
        }
        @media (max-width: ${theme.breakpoints.lgDown}) {
          .navigation-popover__deals {
            width: 525px;
            min-width: 525px;
          }
        }
        @media (max-width: ${theme.breakpoints.mdDown}) {
          .navigation-popover {
            display: none;
          }
        }
      `}</style>
    </>
  );
};

HeaderNavigationPopover.propTypes = {
  category: PropTypes.object,
  setOpen: PropTypes.func,
  showCount: PropTypes.bool,
  site: PropTypes.string,
};

export default HeaderNavigationPopover;
